import { ReceivingLogEditReceipt } from '../../../models/logs/ReceivingLogDetails';
import { ReceiptSubmitRow } from '../../../models/logs/ReceiptSubmitRow';
import { ContainerItem } from '../../../models/logs/ContainerItem';
import {
  ContainerChangesRequest,
  ExpectedRevision,
  LogRowFieldEdits,
  NON_EDITABLE_LOG_ROW_FIELDS,
  ReceiptChangesRequest,
  ReceiptFieldPatch,
  UpdateReceivingLogRequest,
} from '../../../models/logs/UpdateReceivingLogRequest';
import {
  ContainerRow,
  MAX_ITEM_TYPE_LENGTH,
  normalizeItemType,
  toContainerItems,
} from './container-items.rules';
import { NoteReceiptRow, missingNoteClause, recomposeItemsNote } from './log-edit-items-string.rules';

/**
 * receiving-log-save.rules.ts
 *
 * Pure assembly of the `PUT /V1/receiving-log/{logId}` body from the log-edit
 * screen's working state — handoff-18 §2, §3 and §5. Nothing here talks to the
 * network (that is `ReceivingLogApiService.putReceivingLogEdit`) and nothing here
 * interprets a response (that is session 3, §4/§6/§7).
 *
 * The structure of this file is itself a safety mechanism, so it is worth saying
 * out loud what each piece refuses to know:
 *
 * - {@link buildReceiptUpdates} is handed the per-row EDIT MAP and nothing else.
 *   The loaded `ReceivingLogEditReceipt` rows are not in its scope, so it has
 *   nothing to echo back — the §3.1 hazard (a wholesale row round-trip re-opening
 *   a closed PO line via `completesLine: false`, or erasing damage via
 *   `isDamaged: false`) is not merely avoided here, it is unreachable.
 * - {@link LogEditSource} has NO `items` member. §5.1's "easiest thing on the
 *   page to get wrong" is `expected.activeItemIds` being built from
 *   `details.items.map(...)` — which includes tombstones, and produces a `409`
 *   loop that survives every reload on any log that has ever had an item voided.
 *   {@link buildExpectedRevision} cannot express that mistake: the array it
 *   would need is not on the type it is given.
 * - {@link LogRowFieldEdits} omits `poNumber`/`id`/`quantity`/`items` at the type
 *   level, and {@link scrubLogRowFields} deletes them again at runtime for the
 *   benefit of anything that arrived through an `any` (§3.5).
 * - Nothing in this file mints an `itemId`. Container additions are
 *   `(quantity, itemType)` pairs only (§3.6) — deliberately unlike the submit
 *   path, where client-minted ids are honoured so labels can print in parallel.
 */

// #region Working state

/**
 * The fields of ONE existing receipt row the user has actually changed — the
 * unit of §3.1's sparse patch. An absent key means "not touched, leave the
 * stored value alone"; that is the whole point of the type, so never populate it
 * by spreading a loaded row into it.
 *
 * A `null` value is treated the same as absent when the patch is built: this
 * endpoint writes only non-null fields, so `null` cannot clear anything and
 * sending it would just be noise on the wire (§3.1). If a genuine clear is ever
 * needed, `''` is the closest available move and a real `null` clear has to be
 * raised as a blocker.
 */
export interface ReceiptRowEdits {
  quantityReceived?: number;
  /** Only ever `'Complete'` or `'Partial'`. `'Missing'` is not a stored value — a shortfall is derived at read time. */
  receiptStatus?: 'Complete' | 'Partial';
  /** ⚠️ Set ONLY from a real user action. `false` on a row that was closed RE-OPENS the PO line. */
  completesLine?: boolean;
  notes?: string;
  /** ⚠️ Set ONLY from a real user action. `false` on a damaged row erases the damage while its photos stay attached. */
  isDamaged?: boolean;
  /** ⚠️ Sending this REWRITES `isDamaged` from it (`> 0`), so `0` clears the flag you never mentioned. */
  damagedQuantity?: number;
  damageNote?: string;
  damageDisposition?: 'Refuse' | 'Return' | 'Keep';
  itemNumber?: string;
  itemDescription?: string;
  unitOfMeasure?: string;
  quantityOrdered?: number;
  poLineSequence?: number;
}

/** Per-receipt-id edits, keyed by the loaded row's `id`. Read-only so a builder cannot accumulate into it. */
export type ReceiptEditMap = ReadonlyMap<number, ReceiptRowEdits>;

/**
 * Everything the edit screen is holding that isn't already stored. Mutated in
 * place by the component's (click)/(ngModel) handlers; translated to a request
 * body by {@link buildLogEditRequest}, which never reads component state itself.
 */
export interface LogEditWorkingState {
  /** Sparse `MM.ReceivingLog` field edits. Empty object = no field edits, which is a legitimate save (§4.4). */
  logRow: LogRowFieldEdits;
  /** Only the fields the user touched, per receipt id (§3.1). */
  receiptEdits: Map<number, ReceiptRowEdits>;
  /** Brand-new receipt rows. The API validates NOTHING on these — see {@link validateAddedReceiptRows} (§3.2). */
  addedReceipts: ReceiptSubmitRow[];
  deletedReceiptIds: number[];
  /** Item GUIDs selected for tombstoning, taken from the ACTIVE item rows (§3.3). */
  voidItemIds: string[];
  /** Free text stored verbatim; blank means "no reason given" and nothing is invented (§3.3). */
  voidReason: string;
  /** Extra containers that arrived. Duplicate types are LEGAL here and merge (§3.4). */
  addedContainers: ContainerRow[];
  /**
   * Whether the PO ITSELF is complete — `MM.ReceivingLogPoStatus`. `null` leaves the
   * row alone.
   *
   * Keyed BY PO, one row for the whole order, so this is a statement about the PO
   * made from one delivery's screen. Deliberately SEPARATE from
   * {@link logCompletesPo} below: they answer different questions and conflating
   * them means dropping this log's claim reopens the entire PO.
   */
  completesPo: boolean | null;

  /**
   * Whether THIS LOG is the delivery that completed the PO — the note's
   * `PO COMPLETE` clause. `null` leaves whatever the stored note said.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * WHY THIS IS NOT THE SAME FIELD AS `completesPo`
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * `MM.ReceivingLogPoStatus` has ONE ROW PER PO and records `completedBy` and
   * `completedDate` — but NOT which log did it. The note's clause is therefore the
   * only per-log record that exists: a log whose note reads `PO COMPLETE` is the
   * delivery that claimed the PO was finished.
   *
   * So there are two independent facts, and a single checkbox cannot express them:
   *
   *   - the PO is complete, but a DIFFERENT delivery finished it → no clause on this
   *     log, status untouched;
   *   - this delivery finished it → clause on this log, AND the status set;
   *   - this delivery's claim was wrong, but the PO is still complete → clause
   *     removed, status LEFT ALONE. Reopening the PO here would throw away a fact
   *     nothing can reconstruct, because no row says which other log completed it.
   */
  logCompletesPo: boolean | null;
  /**
   * The user's `MISSING LINE ITEM(S) …` text, or `null` for "not touched".
   *
   * That clause is the one part of the note nothing can recompute — it came from
   * the user's own missing-page selections at submit, and those inputs were never
   * persisted (see `log-edit-items-string.rules.ts`). So it is a human judgment
   * at edit time, held here, and taken verbatim onto the wire.
   *
   * `null` preserves the stored clause exactly. `''` DROPS it, which is a real
   * edit ("nothing is owed after all"), not an omission.
   */
  missingNote: string | null;
}

/** A fresh, untouched working state. */
export function emptyLogEditState(): LogEditWorkingState {
  return {
    logRow: {},
    receiptEdits: new Map<number, ReceiptRowEdits>(),
    addedReceipts: [],
    deletedReceiptIds: [],
    voidItemIds: [],
    voidReason: '',
    addedContainers: [],
    completesPo: null,
    logCompletesPo: null,
    // null, not '': an untouched screen must not silently delete a shortfall.
    missingNote: null,
  };
}

/**
 * The slice of `GET …/details` a save is built from.
 *
 * ⚠️ `items` — the per-unit item rows array — is DELIBERATELY NOT ON THIS TYPE.
 * It contains voided (tombstoned) rows, and `expected.activeItemIds` built from
 * it is a permanent `409` loop (§5.1). The response's own `activeItemIds` field
 * is the only correct source and is the only one reachable from here.
 *
 * `log.items` is unrelated to that array despite the name collision — it is the
 * note TEXT, and it is here so {@link buildLogEditRequest} can preserve the part
 * of the note the receipt rows cannot rebuild (see `log-edit-items-string.rules.ts`).
 */
export interface LogEditSource {
  /** The ACTIVE subset of item ids, verbatim from the response. Never re-derived. */
  activeItemIds: string[];
  receipts: ReceivingLogEditReceipt[];
  log: {
    /** Read only to gate the PO-keyed parts of an edit (§4.3). Never written — a log's PO is not editable (§3.5). */
    poNumber?: string | null;
    /** The stored note TEXT. Not the item rows. */
    items?: string | null;
  };
}

// #endregion

// #region Request assembly

/**
 * §5.1, and the reason this function exists at all rather than being two inline
 * `.map()` calls: `activeItemIds` is passed straight through from the response,
 * and the only array of item rows that could be mapped instead is not on
 * {@link LogEditSource}.
 *
 * Order is irrelevant — the backend compares these as sets — and each list is
 * checked only if sent, but both are always sent from here. Omitting `expected`
 * entirely skips the check, which would let a second open edit screen silently
 * reinstate numbering onto labels already stuck to boxes.
 */
export function buildExpectedRevision(source: LogEditSource): ExpectedRevision {
  return {
    activeItemIds: [...(source.activeItemIds ?? [])],
    receiptIds: (source.receipts ?? []).map((receipt) => receipt.id),
  };
}

/**
 * Turns the per-row edit map into §3.1's sparse `receipts.updated` array.
 *
 * Takes the EDIT MAP ONLY. It has no access to the loaded rows, which is what
 * makes a wholesale echo structurally impossible rather than merely discouraged.
 *
 * Keys whose value is `undefined` or `null` are dropped (a `null` cannot clear a
 * field through this endpoint, so it would be noise), and a row whose edits all
 * drop out is omitted entirely rather than sent as a bare `{ id }` no-op.
 *
 * @param edits Per-receipt-id maps of ONLY the fields the user changed.
 * @returns One patch per row that has at least one real change, each carrying its id.
 */
export function buildReceiptUpdates(edits: ReceiptEditMap): ReceiptFieldPatch[] {
  const patches: ReceiptFieldPatch[] = [];

  edits.forEach((rowEdits, id) => {
    const changed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(rowEdits ?? {})) {
      if (value === undefined || value === null) { continue; }
      changed[key] = value;
    }
    if (Object.keys(changed).length === 0) { return; }
    patches.push({ id, ...changed } as ReceiptFieldPatch);
  });

  return patches;
}

/**
 * Strips the four fields that must never appear on `logRow` (§3.5) plus any
 * `undefined`/`null` values, and hands back a fresh object.
 *
 * {@link LogRowFieldEdits} already makes those four uncompilable, so this is the
 * runtime belt to that type-level braces: a row that arrived through an `any`
 * (an echoed-back GET row, a spread of `details.log`) still cannot carry
 * `poNumber` — which would fail the ENTIRE save with a `400` — or `id`/`quantity`,
 * which are silently ignored and would make a real field edit come back as
 * `logRowStatus: "No log-row fields to update"`.
 */
export function scrubLogRowFields(logRow: LogRowFieldEdits | null | undefined): LogRowFieldEdits {
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(logRow ?? {})) {
    if (NON_EDITABLE_LOG_ROW_FIELDS.includes(key)) { continue; }
    if (value === undefined || value === null) { continue; }
    scrubbed[key] = value;
  }
  return scrubbed as LogRowFieldEdits;
}

/**
 * The receipt rows as they will be AFTER this save: loaded rows with their
 * patches applied and deletions removed, then the additions appended.
 *
 * Used ONLY to recompose the note text (§2). This is the one place the loaded
 * rows and the edit map meet, and it is deliberately not the place the
 * `receipts.updated` array is built — the two have opposite requirements (the
 * note needs whole rows, the patch must contain nothing the user didn't touch).
 */
export function effectiveReceiptRows(
  loadedRows: ReceivingLogEditReceipt[],
  edits: ReceiptEditMap,
  addedRows: ReceiptSubmitRow[],
  deletedIds: number[],
): NoteReceiptRow[] {
  const deleted = new Set(deletedIds ?? []);

  const survivors: NoteReceiptRow[] = (loadedRows ?? [])
    .filter((row) => !deleted.has(row.id))
    .map((row) => {
      const rowEdits = edits.get(row.id);
      if (!rowEdits) { return row; }
      const merged: NoteReceiptRow = { ...row };
      for (const [key, value] of Object.entries(rowEdits)) {
        if (value === undefined || value === null) { continue; }
        (merged as Record<string, unknown>)[key] = value;
      }
      return merged;
    });

  const added: NoteReceiptRow[] = (addedRows ?? []).map((row) => ({
    poLineSequence: row.poLineSequence,
    itemNumber: row.itemNumber,
    itemDescription: row.itemDescription,
    quantityReceived: row.quantityReceived,
    unitOfMeasure: row.unitOfMeasure,
    receiptStatus: row.receiptStatus,
    completesLine: row.completesLine,
    isDamaged: row.isDamaged ?? false,
    damagedQuantity: row.damagedQuantity ?? null,
    damageNote: row.damageNote ?? null,
    damageDisposition: row.damageDisposition ?? null,
  }));

  return [...survivors, ...added];
}

/** Container additions on the wire: normalized type, whole quantity ≥ 1, blanks dropped. Same mapping the submit uses. */
export function toAddedContainerItems(rows: ContainerRow[]): ContainerItem[] {
  return toContainerItems(rows);
}

/**
 * Assembles the whole `PUT` body from the working state.
 *
 * Every section is omitted when it has nothing in it — a `PUT` carrying only
 * `editedBy`/`expected` is a legitimate `200` that writes nothing (§4.4), so
 * there is no need for (and must be no) dummy field edit to avoid it.
 *
 * `items` is sent exactly when the receipt rows changed and omitted when they
 * didn't (§2): omitting leaves the stored text alone, which is right for an edit
 * that touched no receipts and wrong for one that did. It is never sent as
 * `''`-to-mean-omitted — an empty string writes an empty note, which is a real
 * edit (and IS what deleting every receipt row should produce).
 *
 * `quantity` appears nowhere: the backend derives it from the surviving item rows
 * inside the same transaction and returns it on `quantityDisplay` (§2, §6.1).
 *
 * @param source The loaded details, narrowed to what a save may read (see {@link LogEditSource}).
 * @param state The screen's pending edits.
 * @param editedBy Who is saving. REQUIRED by the API; blank is a `400`.
 */
export function buildLogEditRequest(
  source: LogEditSource,
  state: LogEditWorkingState,
  editedBy: string,
): UpdateReceivingLogRequest {
  const updated = buildReceiptUpdates(state.receiptEdits);
  const added = state.addedReceipts ?? [];
  const deletedIds = dedupeNumbers(state.deletedReceiptIds ?? []);
  const receiptsChanged = updated.length > 0 || added.length > 0 || deletedIds.length > 0;
  // The note also goes out when only the shortfall text moved: the user edited the
  // one clause the rows cannot rebuild, and omitting `items` would leave the
  // stored note asserting the number they just corrected.
  //
  // And when only PO completion moved. That writes `MM.ReceivingLogPoStatus`, but
  // the note carries a `PO COMPLETE` clause too — the submit appends one — so
  // omitting `items` here would leave the note and the PO status contradicting each
  // other, in whichever direction the tick went.
  const noteChanged = receiptsChanged
    || missingNoteEdited(source, state)
    || logCompletesPoEdited(state);

  // §7: there is no GP-note field to set. That note is written once, by the
  // submit; no edit path writes it and nothing here may claim one did.
  const request: UpdateReceivingLogRequest = {
    editedBy,
    expected: buildExpectedRevision(source),
  };

  const logRow = scrubLogRowFields(state.logRow);
  if (Object.keys(logRow).length > 0) {
    request.logRow = logRow;
  }

  if (receiptsChanged) {
    const receipts: ReceiptChangesRequest = {};
    // Both `updated` and `added` deserialize into the same model, whose PoNumber is
    // non-nullable — so BOTH need the log's PO or the request is refused outright.
    // Stamped here, in the one layer that knows the log, and never by
    // `buildReceiptUpdates`, which is deliberately given no access to anything but
    // the user's own edits.
    const poNumber = (source.log?.poNumber ?? '').trim();
    if (updated.length > 0) {
      receipts.updated = updated.map((patch) => ({ ...patch, poNumber }));
    }
    if (added.length > 0) {
      receipts.added = added.map((row) => ({ ...row, poNumber }));
    }
    if (deletedIds.length > 0) { receipts.deletedIds = deletedIds; }
    request.receipts = receipts;
  }

  if (noteChanged) {
    // The note describes the rows; recompose it from the rows the save leaves
    // behind. The shortfall half is NOT recomposed — it is the user's text,
    // passed straight through (or the stored clause when they left it alone).
    request.items = recomposeItemsNote(
      source.log?.items,
      effectiveReceiptRows(source.receipts ?? [], state.receiptEdits, added, deletedIds),
      state.missingNote,
      // `null` means the user did not touch the tick, and the clause is then
      // re-derived from whatever the stored note already said.
      state.logCompletesPo,
    );
  }

  const containers = buildContainerChanges(state);
  if (containers) { request.containers = containers; }

  if (state.completesPo !== null && state.completesPo !== undefined) {
    request.completesPo = state.completesPo;
  }

  return request;
}

/** The `containers` section, or null when no item rows are being voided or added. */
function buildContainerChanges(state: LogEditWorkingState): ContainerChangesRequest | null {
  const voidItemIds = dedupeStrings(state.voidItemIds ?? []);
  const addedItems = toAddedContainerItems(state.addedContainers ?? []);
  if (voidItemIds.length === 0 && addedItems.length === 0) { return null; }

  const containers: ContainerChangesRequest = {};
  if (voidItemIds.length > 0) {
    containers.voidItemIds = voidItemIds;
    const reason = (state.voidReason ?? '').trim();
    // Omitted rather than sent blank: a void with no reason stores null, and
    // nothing is invented to fill it (§3.3).
    if (reason) { containers.voidReason = reason; }
  }
  if (addedItems.length > 0) { containers.added = addedItems; }
  return containers;
}

/**
 * Whether the user moved the PO-completion tick.
 *
 * `null` is "untouched" and is never a change — the same three-state convention
 * `missingNote` uses. Only a real decision reaches the wire, so an ordinary items
 * edit neither stamps `completedBy` on a PO nobody decided about nor rewrites the
 * note's `PO COMPLETE` clause.
 */
export function poCompleteEdited(state: LogEditWorkingState): boolean {
  return state.completesPo !== null && state.completesPo !== undefined;
}

/**
 * Whether the user changed THIS LOG's claim to have completed the PO.
 *
 * Drives whether the note is rewritten. Separate from {@link poCompleteEdited},
 * which drives whether `MM.ReceivingLogPoStatus` is written — a user can move either
 * one without the other, and the whole point of the split is that dropping a claim
 * does not reopen a PO somebody else finished.
 */
export function logCompletesPoEdited(state: LogEditWorkingState): boolean {
  return state.logCompletesPo !== null && state.logCompletesPo !== undefined;
}

/**
 * Whether the user's shortfall text differs from the clause stored on the log.
 *
 * `null` (untouched) is never a change. Compared after trimming so re-typing the
 * same words with a stray space is not treated as an edit — but a CLEARED field
 * against a stored clause is, because deleting the shortfall is a real decision.
 */
export function missingNoteEdited(source: LogEditSource, state: LogEditWorkingState): boolean {
  if (state.missingNote == null) { return false; }
  return state.missingNote.trim() !== missingNoteClause(source.log?.items);
}

/**
 * Whether this edit moved the RECEIVED quantities — which is what can make a
 * stored shortfall wrong.
 *
 * A `receipts.updated` patch carrying `quantityReceived`, a new receipt row, or a
 * deleted one all change how much of the line is accounted for. A notes-only or
 * status-only patch does not, so it must not demand a decision the user has no
 * reason to make.
 */
export function receiptQuantitiesChanged(state: LogEditWorkingState): boolean {
  return (
    buildReceiptUpdates(state.receiptEdits).some((patch) => 'quantityReceived' in patch) ||
    (state.addedReceipts ?? []).length > 0 ||
    (state.deletedReceiptIds ?? []).length > 0
  );
}

/**
 * Whether the save must stop and make the user decide about the shortfall text.
 *
 * This is the load-bearing half of the MISSING-clause fix. The stored clause was
 * never derived from data — it came from the user's own missing-page selections
 * at submit, which were never persisted — so nothing can recompute it when the
 * received quantities move. Change a quantity 6→2 and a preserved
 * `MISSING LINE ITEM 1 (X4)` silently asserts a shortfall of 4 when 8 are really
 * owed. A silent stale number is the defect; requiring a decision is the fix.
 *
 * True only when BOTH halves are present: the quantities moved AND there is a
 * stored clause to go stale. A log whose note records no shortfall has nothing to
 * confirm, and this must not invent a field for the user to fill in.
 */
export function missingClauseNeedsReview(source: LogEditSource, state: LogEditWorkingState): boolean {
  return receiptQuantitiesChanged(state) && missingNoteClause(source.log?.items).length > 0;
}

/** Whether the state holds anything worth sending. A no-op `PUT` is legal, but there is no reason to make one. */
export function hasPendingChanges(source: LogEditSource, state: LogEditWorkingState): boolean {
  return (
    Object.keys(scrubLogRowFields(state.logRow)).length > 0 ||
    buildReceiptUpdates(state.receiptEdits).length > 0 ||
    (state.addedReceipts ?? []).length > 0 ||
    (state.deletedReceiptIds ?? []).length > 0 ||
    (state.voidItemIds ?? []).length > 0 ||
    toAddedContainerItems(state.addedContainers ?? []).length > 0 ||
    poCompleteEdited(state) ||
    logCompletesPoEdited(state) ||
    missingNoteEdited(source, state)
  );
}

// #endregion

// #region Validation

/** Why an added container row can't be sent. Note there is no `'duplicate'` — a repeated type is LEGAL on an edit (§3.4). */
export type AddedContainerError = 'blank' | 'tooLong' | 'quantity';

/**
 * Per-row validation for `containers.added`, mirroring the three `400`s §4.1
 * lists for it (`quantity < 1`, blank `itemType`, `itemType` over 50 chars).
 *
 * Reuses the submit path's `normalizeItemType`/`MAX_ITEM_TYPE_LENGTH` rather
 * than re-deciding what a type is, but deliberately drops the submit path's
 * no-duplicates rule: on an edit a type already on the log is the NORMAL case
 * ("one more box arrived") and merges into that type's count.
 */
export function validateAddedContainerRows(rows: ContainerRow[]): (AddedContainerError | null)[] {
  return (rows ?? []).map((row) => {
    const type = normalizeItemType(row?.itemType);
    if (!type) { return 'blank'; }
    if (type.length > MAX_ITEM_TYPE_LENGTH) { return 'tooLong'; }
    if (!Number.isFinite(row.quantity) || Math.floor(Number(row.quantity)) < 1) { return 'quantity'; }
    return null;
  });
}

/**
 * Validation for `receipts.added`.
 *
 * §3.2: **the API checks nothing at all on these rows.** No required field, no
 * minimum on `quantityReceived`, no allowed-value check on `receiptStatus`, no
 * check of `poLineSequence` against the PO. `{"quantityReceived": 2}` inserts
 * cleanly and lands in the ledger that drives `qtyReceived` and `isClosed` on the
 * PO screen. So this is not a convenience pre-check ahead of a server-side
 * check — it is the only check there is, and it mirrors what
 * `receipt-rows.rules.ts` guarantees at submit time:
 *
 * - a description (submit always fills it from the line or the part name),
 * - `receiptStatus` restricted to `'Complete' | 'Partial' | null` — **never
 *   `'Missing'`**, which is derived at read time and never stored,
 * - `quantityReceived` either null (a part/status-only row) or a finite,
 *   non-negative number (submit floors every quantity at 0).
 *
 * @returns One human-readable problem per offending row; empty when all rows are sendable.
 */
export function validateAddedReceiptRows(rows: ReceiptSubmitRow[]): string[] {
  const problems: string[] = [];

  (rows ?? []).forEach((row, index) => {
    const label = `New receipt ${index + 1}`;
    if (!(row?.itemDescription ?? '').trim()) {
      problems.push(`${label}: a description is required.`);
    }
    if (row?.receiptStatus != null && row.receiptStatus !== 'Complete' && row.receiptStatus !== 'Partial') {
      problems.push(`${label}: status must be Complete or Partial — "Missing" is never stored.`);
    }
    if (row?.quantityReceived != null) {
      const qty = Number(row.quantityReceived);
      if (!Number.isFinite(qty) || qty < 0) {
        problems.push(`${label}: quantity received must be a number of 0 or more.`);
      }
    }
  });

  return problems;
}

/**
 * Everything that would make this save fail, checked before it is sent. Blocking
 * problems only; the UI shows these and does not send.
 *
 * This is not an attempt to replace the server's refusals — session 3 handles a
 * `400` properly — but every entry here is a refusal the screen already has
 * enough information to avoid, and the two §4.3 entries in particular need
 * saying in the UI rather than as a raw API string, because **there is no way to
 * fix them by setting the PO**: a log's PO cannot be changed after submit by any
 * endpoint (§3.5).
 *
 * Note what is NOT blocked on a PO-less log: container voids and additions,
 * `receipts.updated`, `receipts.deletedIds` and ordinary `logRow` edits all work
 * normally there (§4.3), so the editor must not be disabled wholesale.
 */
export function validateLogEditState(
  source: LogEditSource,
  state: LogEditWorkingState,
  editedBy: string,
): string[] {
  const problems: string[] = [];
  const hasPo = !!(source.log?.poNumber ?? '').trim();

  if (!(editedBy ?? '').trim()) {
    problems.push('We could not tell who is making this edit. Sign in again and retry.');
  }

  // §4.3 — all three of these are keyed by PO and have nothing to attach to.
  if (!hasPo && (state.addedReceipts ?? []).length > 0) {
    problems.push(
      'This log has no PO number, so receipt rows cannot be added to it. A log\'s PO cannot be set after ' +
      'it is submitted — a delivery that needs a ledger needs a log submitted against a PO.',
    );
  }
  if (!hasPo && state.completesPo !== null && state.completesPo !== undefined) {
    problems.push('This log has no PO number, so PO completion cannot be recorded from it.');
  }

  const activeIds = new Set(source.activeItemIds ?? []);
  const voidIds = dedupeStrings(state.voidItemIds ?? []);
  const unknownVoids = voidIds.filter((id) => !activeIds.has(id));
  if (unknownVoids.length > 0) {
    problems.push(
      `These items are no longer active on this log: ${unknownVoids.join(', ')}. Reload the log — someone else changed it.`,
    );
  }
  // §4.1 — a void that would leave the log with no active items is refused;
  // delete the log instead. A legacy log with zero item rows to begin with is
  // not refused, so only guard the case where there is something to empty.
  if (
    activeIds.size > 0 &&
    voidIds.length >= activeIds.size &&
    unknownVoids.length === 0 &&
    toAddedContainerItems(state.addedContainers ?? []).length === 0
  ) {
    problems.push('Voiding every item would leave this log with nothing on it. Delete the log instead.');
  }

  const receiptIds = new Set((source.receipts ?? []).map((r) => r.id));
  const touchedIds = [
    ...buildReceiptUpdates(state.receiptEdits).map((patch) => patch.id),
    ...dedupeNumbers(state.deletedReceiptIds ?? []),
  ];
  const foreignReceipts = touchedIds.filter((id) => !receiptIds.has(id));
  if (foreignReceipts.length > 0) {
    problems.push(
      `These receipt rows are not on this log: ${foreignReceipts.join(', ')}. Reload the log and redo the edit.`,
    );
  }

  validateAddedContainerRows(state.addedContainers ?? []).forEach((error, index) => {
    if (error === 'blank') { problems.push(`Added container ${index + 1}: pick or type a container type.`); }
    if (error === 'tooLong') { problems.push(`Added container ${index + 1}: the type must be ${MAX_ITEM_TYPE_LENGTH} characters or fewer.`); }
    if (error === 'quantity') { problems.push(`Added container ${index + 1}: the quantity must be at least 1.`); }
  });

  problems.push(...validateAddedReceiptRows(state.addedReceipts ?? []));

  return problems;
}

// #endregion

// #region Pre-save warnings

/** One item row's identity for the quantity preview — the active-row facts a void selection is made against. */
export interface PreviewItemRow {
  itemId: string;
  itemType: string | null;
}

/**
 * The quantity string this edit will produce, for a before/after shown BEFORE
 * saving.
 *
 * PREVIEW ONLY, exactly like `composeQuantityDisplay` at submit: the persisted
 * value always comes from the backend and comes back on `quantityDisplay` (§6.1),
 * which is what the screen must then display. This exists because §6.1 requires
 * showing the before and after on a legacy log that is gaining containers —
 * deriving the quantity REPLACES that log's free-text record of what arrived
 * ("3 boxes" → "1 PALLET"), and the user has to see that before it happens, not
 * after.
 *
 * @param activeItems The log's ACTIVE item rows (voided rows are already excluded upstream).
 * @param voidItemIds The ids selected for voiding.
 * @param addedContainers Pending container additions.
 */
export function previewQuantityAfterEdit(
  activeItems: PreviewItemRow[],
  voidItemIds: string[],
  addedContainers: ContainerRow[],
): string {
  const voided = new Set(voidItemIds ?? []);
  const counts = new Map<string, number>();

  for (const item of activeItems ?? []) {
    if (voided.has(item.itemId)) { continue; }
    const type = normalizeItemType(item.itemType);
    if (!type) { continue; }
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }

  for (const added of toAddedContainerItems(addedContainers ?? [])) {
    counts.set(added.itemType, (counts.get(added.itemType) ?? 0) + added.quantity);
  }

  return [...counts.entries()]
    .map(([type, quantity]) => `${quantity} ${type.toUpperCase()}`)
    .join(', ');
}

/*
 * There is deliberately no `willRenumberLabels(state)` here any more.
 *
 * It answered "does this edit move item rows around" from the working state's
 * container fields alone, and the edit screen called it on the ITEMS arm of a
 * ternary — where those fields are permanently empty, because the container editor
 * is quantity-mode only and is the one thing that writes them. So it was a constant
 * `false` at its only call site.
 *
 * `planRenumbersLabels` in `container-plan.rules.ts` is the real test and the only
 * one now: it compares the plan against the log's ACTIVE rows, so it knows that
 * voiding an entire type renumbers nothing and that adding a brand-new type
 * renumbers nothing either — distinctions this could not make (§3.4). The
 * authoritative list of what actually moved is still `renumberedItems` on the
 * response.
 */

// #endregion

// #region Helpers

function dedupeNumbers(values: number[]): number[] {
  return [...new Set(values)];
}

function dedupeStrings(values: string[]): string[] {
  return [...new Set(values)];
}

// #endregion
